import { useCallback, useId, useMemo, useState } from 'react';
import { AssetRequestError } from '../api/getAssetById';
import { fetchProfile, postProfileCreditsTopUp } from '../api/profileApi';
import { getStoredLoginIdentifier } from '../auth/sessionToken';
import { useI18n } from '../i18n';
import {
  extractCreditsBalanceFromApiPayload,
  rememberPrivateClientWalletCredits,
} from '../utils/privateClientCredits';
import { assertProfileMutationSuccess } from '../utils/profileWorkerList';
import { BookingPaymentPanel } from './BookingPaymentPanel';
import pageThemeStyles from './menuPageTheme.module.css';
import styles from './ProfileMenu.module.css';

const CREDIT_PACKS = [500, 1000, 2000] as const;
const CUSTOM_PACK_ID = 'custom' as const;
const MIN_CUSTOM_CREDITS = 1;

type PackSelection = (typeof CREDIT_PACKS)[number] | typeof CUSTOM_PACK_ID;

function formatShekelAmount(amount: number): string {
  return `₪${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function parseCustomCredits(raw: string): number | null {
  const digits = raw.replace(/[^\d]/g, '');
  if (!digits) {
    return null;
  }
  const value = Number.parseInt(digits, 10);
  if (!Number.isFinite(value) || value < MIN_CUSTOM_CREDITS) {
    return null;
  }
  return value;
}

export type ProfileLoadCreditsPanelProps = {
  currentCredits: number;
  onBack: () => void;
  /** Called after a successful top-up with the new wallet balance. */
  onLoaded?: (nextCredits: number) => void;
};

export function ProfileLoadCreditsPanel({
  currentCredits,
  onBack,
  onLoaded,
}: ProfileLoadCreditsPanelProps) {
  const { t, language } = useI18n();
  const customFieldId = useId();
  const [selectedPack, setSelectedPack] = useState<PackSelection>(1000);
  const [customAmountInput, setCustomAmountInput] = useState('');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customCredits = useMemo(() => parseCustomCredits(customAmountInput), [customAmountInput]);

  const selectedCredits = selectedPack === CUSTOM_PACK_ID ? customCredits : selectedPack;

  const amountLabel =
    selectedCredits != null ? formatShekelAmount(selectedCredits) : null;

  const canContinue = selectedCredits != null && selectedCredits >= MIN_CUSTOM_CREDITS;

  const textDir = language === 'he' ? 'rtl' : 'ltr';

  const onContinueToPayment = useCallback(() => {
    if (!canContinue) {
      if (selectedPack === CUSTOM_PACK_ID) {
        setError(t('profile.hub.loadCreditsCustomInvalid'));
      }
      return;
    }
    setError(null);
    setPaymentOpen(true);
  }, [canContinue, selectedPack, t]);

  const onPaymentCancelled = useCallback(() => {
    if (processing) {
      return;
    }
    setPaymentOpen(false);
  }, [processing]);

  const onPaymentConfirmed = useCallback(async () => {
    if (selectedCredits == null) {
      return { success: false, errorMessage: t('common.somethingWrong') };
    }
    setProcessing(true);
    setError(null);
    try {
      // Card / Bit / PayPal UI is demo only (no gateway). Top-up runs after that step succeeds.
      const response = await postProfileCreditsTopUp(selectedCredits);
      assertProfileMutationSuccess(response, t('profile.hub.loadCreditsTitle'));
      let next =
        extractCreditsBalanceFromApiPayload(response) ?? currentCredits + selectedCredits;
      try {
        const refreshed = await fetchProfile();
        const fromRefresh = extractCreditsBalanceFromApiPayload(refreshed);
        if (fromRefresh !== null) {
          next = fromRefresh;
        }
      } catch {
        // Keep balance from top-up response / local sum if refresh fails.
      }
      rememberPrivateClientWalletCredits(next, getStoredLoginIdentifier());
      onLoaded?.(next);
      setPaymentOpen(false);
      onBack();
      return { success: true };
    } catch (err) {
      const message =
        err instanceof AssetRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('common.somethingWrong');
      setError(message);
      return { success: false, errorMessage: message };
    } finally {
      setProcessing(false);
    }
  }, [currentCredits, onBack, onLoaded, selectedCredits, t]);

  return (
    <div
      className={[pageThemeStyles.wrapEmbedded, pageThemeStyles.wrapThemed].join(' ')}
      role="region"
      aria-label={t('profile.hub.loadCreditsTitle')}
    >
      <div className={[pageThemeStyles.panelEmbedded, pageThemeStyles.browsePage].join(' ')}>
        <div className={styles.hubSubPanel} dir={textDir}>
          <div className={styles.hubTopBar}>
            <button
              type="button"
              className={styles.hubTopBarBtn}
              onClick={onBack}
              aria-label={t('profile.hub.back')}
            >
              <ProfileBackIcon rtl={language === 'he'} />
            </button>
            <h2 className={styles.hubTopBarTitle}>{t('profile.hub.loadCreditsTitle')}</h2>
            <span className={styles.hubTopBarSpacer} aria-hidden />
          </div>

          <p className={styles.hubSubPanelText}>{t('profile.hub.loadCreditsIntro')}</p>

          <p className={styles.hubSubPanelMeta} role="status">
            {t('profile.hub.loadCreditsBalance', {
              amount: currentCredits.toLocaleString(language === 'he' ? 'he-IL' : 'en-US', {
                maximumFractionDigits: 2,
              }),
            })}
          </p>

          <fieldset className={styles.loadCreditsPacks}>
            <legend className={styles.loadCreditsPacksLegend}>
              {t('profile.hub.loadCreditsChoose')}
            </legend>
            <div className={styles.loadCreditsPacksList}>
              {CREDIT_PACKS.map((credits) => {
                const selected = selectedPack === credits;
                return (
                  <button
                    key={credits}
                    type="button"
                    className={[
                      styles.loadCreditsPackBtn,
                      selected ? styles.loadCreditsPackBtnActive : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-pressed={selected}
                    onClick={() => {
                      setSelectedPack(credits);
                      setError(null);
                    }}
                  >
                    <span className={styles.loadCreditsPackCredits}>
                      {t('profile.hub.loadCreditsPackCredits', { count: credits })}
                    </span>
                    <span className={styles.loadCreditsPackPrice} dir="ltr">
                      {formatShekelAmount(credits)}
                    </span>
                  </button>
                );
              })}

              <button
                type="button"
                className={[
                  styles.loadCreditsPackBtn,
                  selectedPack === CUSTOM_PACK_ID ? styles.loadCreditsPackBtnActive : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-pressed={selectedPack === CUSTOM_PACK_ID}
                onClick={() => {
                  setSelectedPack(CUSTOM_PACK_ID);
                  setError(null);
                }}
              >
                <span className={styles.loadCreditsPackCredits}>
                  {t('profile.hub.loadCreditsCustom')}
                </span>
                <span className={styles.loadCreditsPackPrice} dir="ltr">
                  {customCredits != null ? formatShekelAmount(customCredits) : '—'}
                </span>
              </button>
            </div>

            {selectedPack === CUSTOM_PACK_ID ? (
              <div className={styles.loadCreditsCustomField}>
                <label className={styles.profileEditLabel} htmlFor={customFieldId}>
                  {t('profile.hub.loadCreditsCustomAmount')}
                </label>
                <div className={styles.loadCreditsCustomInputRow}>
                  <span className={styles.loadCreditsCurrency} aria-hidden>
                    ₪
                  </span>
                  <input
                    id={customFieldId}
                    className={[styles.profileEditInput, styles.profileEditInputLtr].join(' ')}
                    dir="ltr"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder={t('profile.hub.loadCreditsCustomPlaceholder')}
                    value={customAmountInput}
                    onChange={(event) => {
                      setCustomAmountInput(event.target.value.replace(/[^\d]/g, ''));
                      setError(null);
                    }}
                    aria-invalid={error ? true : undefined}
                  />
                </div>
              </div>
            ) : null}
          </fieldset>

          {error ? (
            <p className={styles.profileEditError} role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="button"
            className={styles.profileEditSaveBtn}
            onClick={onContinueToPayment}
            disabled={!canContinue}
          >
            {t('profile.hub.loadCreditsContinue')}
          </button>
        </div>
      </div>

      {paymentOpen && amountLabel && selectedCredits != null ? (
        <BookingPaymentPanel
          amountLabel={amountLabel}
          processing={processing}
          onPay={onPaymentConfirmed}
          onCancel={onPaymentCancelled}
        />
      ) : null}
    </div>
  );
}

function ProfileBackIcon({ rtl }: { rtl: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path
        d={rtl ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
